import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';
import {
  classifyProfileSettingsHttpStatus,
  readReaderSettingsFromProfileBody,
  serializeReaderDisplaySettings,
  type ProfileReaderSettingsRead,
  type ReaderDisplaySettings,
} from '@/features/reader/components/reader-display-settings/utils/reader-display-settings';

const PROFILE_SETTINGS_TIMEOUT_MS = 8000;

function createTimeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }

  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

export async function fetchProfileReaderSettingsReq(
  accessToken: string,
): Promise<ProfileReaderSettingsRead> {
  try {
    const response = await fetch(buildDirectBackendUrl('/api/users/me'), {
      method: 'GET',
      headers: createDirectBackendHeaders({ accessToken }),
      signal: createTimeoutSignal(PROFILE_SETTINGS_TIMEOUT_MS),
    });

    if (classifyProfileSettingsHttpStatus(response.status) === 'unavailable') {
      return { status: 'unavailable' };
    }

    const body: unknown = await response.json();
    return readReaderSettingsFromProfileBody(body);
  } catch {
    return { status: 'unavailable' };
  }
}

export async function saveProfileReaderSettingsReq(
  accessToken: string,
  settings: ReaderDisplaySettings,
): Promise<boolean> {
  try {
    const response = await fetch(buildDirectBackendUrl('/api/users/profile'), {
      method: 'PATCH',
      headers: createDirectBackendHeaders({
        accessToken,
        hasJsonContentType: true,
      }),
      body: JSON.stringify({
        readerSettings: serializeReaderDisplaySettings(settings),
      }),
      signal: createTimeoutSignal(PROFILE_SETTINGS_TIMEOUT_MS),
    });

    return response.ok;
  } catch {
    return false;
  }
}
